import { useHammerCheckoutData } from '@liveauctioneers/hammer-ui-payments/hooks';
import { MarketplaceProps, ShippingMethod } from '@liveauctioneers/hammer-ui-payments/types';
import { useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogTitle } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { loadCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.actions';
import { getCheckoutShippingQuote } from '@/redux/modules/checkoutShippingQuote/checkoutShippingQuote.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { getCatalogTermsUrl, getSellerUrl, getShippingPartnerPolicyUrl } from '@/utils/urls';

type Props = {
    setMarketplaceProps: (marketplaceProps: MarketplaceProps) => void;
};

export const useGetMarketplaceProps = ({ setMarketplaceProps }: Props) => {
    const {
        auctionId: catalogId,
        externalId: invoiceId,
        seller,
        shippingMethod,
        shippingPrices,
    } = useHammerCheckoutData();
    const shippingQuote = useAppSelector(getCheckoutShippingQuote);
    const catalogTitle = useAppSelector((state) => getCatalogTitle(state, Number(catalogId)));
    const baseUrl = useAppSelector(getBaseUrl);
    const dispatch = useAppDispatch();
    const sellerUrl = getSellerUrl(Number(seller.id), seller.organizationName);

    const hasLiveShipping = useMemo(
        () => Boolean(shippingPrices?.find((price) => price.method === ShippingMethod.LAShipping)),
        [shippingPrices]
    );

    useEffect(() => {
        if (shippingQuote.shippingProvider === 0 && hasLiveShipping) {
            dispatch(loadCheckoutShippingQuote(Number(invoiceId)));
        }
    }, [dispatch, hasLiveShipping, invoiceId, shippingMethod, shippingQuote]);

    useEffect(() => {
        const liveShippingTermsUrl = getShippingPartnerPolicyUrl(shippingQuote.shippingProvider);

        setMarketplaceProps({
            auctioneerTermsUrl: `${baseUrl}${getCatalogTermsUrl(Number(catalogId), catalogTitle)}`,
            liveShippingTermsUrl: liveShippingTermsUrl
                ? `${baseUrl}${getShippingPartnerPolicyUrl(shippingQuote.shippingProvider)}`
                : '',
            sellerUrl,
            shippingProviderId: shippingQuote.shippingProvider,
        });
    }, [baseUrl, catalogId, catalogTitle, setMarketplaceProps, sellerUrl, shippingQuote.shippingProvider]);
};
